import { closeModal, openModal } from './navigationController';
import { byId } from './domUtils';
import type { Root } from './prototypeTypes';
import { getRuntime } from './runtimeAccessor';
import { ensureTopic, getTopic, type TopicRecord } from './topicStore';
import { attachChapterDirectoryDismissGesture } from './chapterDirectoryGesture';
import { topicPosterSurfaceMarkup } from '../../shared/topicPosterContent';
import { chapterDirectoryRowMarkup, type ChapterDirectoryRowData } from '../../shared/chapterDirectoryRow';
import { topicCategoryLabels } from '../../shared/topicCategoryLabels';
import { topicFavoriteVersion, topicVoteVersion } from './topicFavoriteVersion';
import { applyTopicCover } from './topicCoverLoader';
import { categoriesFromClassification, coverUrlForTopic } from '../../domain/topicCover';
import { getTopicNavigationPreview } from './topicNavigationPreview';

export type DirectoryRow = ChapterDirectoryRowData;
type DirectorySnapshot = {
	slug: string;
	title: string;
	subtitle?: string;
	category?: string;
	classification?: TopicRecord['classification'];
	source?: TopicRecord['source'];
	coverBadge?: 'github' | 'new' | 'trending';
	audioMinutes?: number;
	coverImageUrl?: string;
	rows: DirectoryRow[];
};

function holder(root: Root): { __chapterDirectoryRequest?: number; __chapterDirectoryReactionRequest?: number } {
	return root as unknown as { __chapterDirectoryRequest?: number; __chapterDirectoryReactionRequest?: number };
}

export async function openChapterDirectory(root: Root, returnFocus?: HTMLElement | null): Promise<void> {
	const runtime = getRuntime(root);
	const slug = runtime?.activeCourseId;
	if (!slug) return;
	const request = (holder(root).__chapterDirectoryRequest ?? 0) + 1;
	holder(root).__chapterDirectoryRequest = request;
	const currentIndex = runtime?.activeChapterIndex ?? 1;
	const pageModel = readPageModelDirectory(root, slug);
	const cached = directoryFromTopic(slug, mergeTopicPresentation(getTopic(slug), getTopicNavigationPreview(root, slug)));
	const initialDirectory = mergeDirectory(pageModel, cached);
	if (initialDirectory) paintDirectory(root, initialDirectory, currentIndex);
	else paintDirectoryLoading(root);
	const modal = byId(root, 'chapterDirectoryModal');
	if (!modal?.classList.contains('active')) openModal(root, 'chapterDirectoryModal', returnFocus ?? byId(root, 'articleDirectoryBtn'));
	// Swipe-down dismissal, same grammar as the now-playing panel. Attached
	// once per sheet element; the module owns its own idempotency.
	const sheet = modal?.querySelector('.chapter-directory-sheet');
	if (sheet) attachChapterDirectoryDismissGesture(sheet as HTMLElement, () => closeChapterDirectory(root));
	if (initialDirectory) {
		void revalidateDirectoryLocks(root, request, slug, currentIndex);
		return;
	}
	// The one-Topic navigation projection is an enhancement. Its failure must not
	// prevent a private Topic from asking the existing per-topic chapter endpoint.
	await ensureTopic(slug).catch(() => undefined);
	if (!isCurrentDirectoryRequest(root, request, slug)) return;
	const indexed = directoryFromTopic(slug, getTopic(slug));
	if (indexed) {
		paintDirectory(root, indexed, currentIndex);
		return;
	}
	try {
		const response = await runtime.api.listTopicChapters(slug);
		if (!isCurrentDirectoryRequest(root, request, slug)) return;
		const directory = directoryFromResponse(response, slug);
		if (directory) paintDirectory(root, directory, currentIndex);
		else paintDirectoryError(root);
	} catch {
		if (isCurrentDirectoryRequest(root, request, slug)) paintDirectoryError(root);
	}
}

export function closeChapterDirectory(root: Root): void {
	const modal = byId(root, 'chapterDirectoryModal');
	if (!modal?.classList.contains('active')) return;
	holder(root).__chapterDirectoryRequest = (holder(root).__chapterDirectoryRequest ?? 0) + 1;
	closeModal(root, 'chapterDirectoryModal');
}

function paintDirectory(root: Root, snapshot: DirectorySnapshot | null, currentIndex: number): void {
	const list = byId(root, 'chapterDirectoryList');
	const hero = list?.parentElement?.querySelector<HTMLElement>('.chapter-directory-hero');
	if (!list) return;
	if (!snapshot) {
		list.replaceChildren();
		return;
	}
	// The PC dialog carries the topic identity in the header sub-line
	// (prototype: "{topic} · N chapters"); the poster hero is phone-only.
	const topicLine = byId(root, 'chapterDirectoryTopicLine');
	if (topicLine) {
		topicLine.textContent = `${snapshot.title} · ${snapshot.rows.length} ${snapshot.rows.length === 1 ? 'chapter' : 'chapters'}`;
	}
	// Directory mode renders the viewed page's topic hero: the poster the
	// sheet opened from, not the playing topic's artwork. The cover artwork
	// already carries the topic name, so the sheet has no separate title.
	const like = Array.from(root.querySelectorAll<HTMLElement>('.like-btn')).find((button) => button.dataset.topicId === snapshot.slug);
	const dislike = Array.from(root.querySelectorAll<HTMLElement>('.dislike-btn')).find((button) => button.dataset.topicId === snapshot.slug);
	const favorite = Array.from(root.querySelectorAll<HTMLElement>('.fav-btn')).find((button) => button.dataset.topicId === snapshot.slug);
	const voteValue = like?.getAttribute('aria-pressed') === 'true' ? 1 : dislike?.getAttribute('aria-pressed') === 'true' ? -1 : 0;
	const summedAudioMinutes = snapshot.rows.reduce((total, row) => total + Number(row.duration?.match(/\d+/u)?.[0] ?? 0), 0);
	const audioMinutes = typeof snapshot.audioMinutes === 'number' && snapshot.audioMinutes > 0 ? snapshot.audioMinutes : summedAudioMinutes;
	if (hero) {
		hero.classList.add('topic-poster-surface', 'topic-poster-surface--cover');
		hero.innerHTML = topicPosterSurfaceMarkup({
			poster: {
				title: snapshot.title,
				subtitle: snapshot.subtitle,
				categoryLabels: topicCategoryLabels(snapshot.category, snapshot.classification),
				chapterCount: snapshot.rows.length,
				audioMinutes,
				coverBadge: snapshot.coverBadge ?? (snapshot.source === 'github_trending' ? 'github' : undefined),
				topicId: snapshot.slug,
				voteValue,
				isFavorite: favorite?.getAttribute('aria-pressed') === 'true' || favorite?.classList.contains('active') === true,
				titleTag: 'h2',
				metricVariant: 'topic-hero',
				categoryVariant: 'topic-hero',
				ids: { title: 'chapterDirectoryTitle', subtitle: 'chapterDirectorySubtitle', category: 'chapterDirectoryCategory', metrics: 'chapterDirectoryMetrics' },
			},
			coverId: 'chapterDirectoryHeroPoster',
			coverClass: 'chapter-directory-hero-poster',
			contentClass: 'chapter-directory-hero-copy',
		});
		const cover = hero.querySelector<HTMLElement>('#chapterDirectoryHeroPoster');
		if (cover) void applyTopicCover(cover, snapshot.coverImageUrl ?? coverUrlForTopic(categoriesFromClassification(snapshot.classification)));
	}
	paintDirectoryRows(root, snapshot, currentIndex);
	loadDirectoryReactions(root, snapshot.slug, hero ?? null);
}

function paintDirectoryRows(root: Root, snapshot: DirectorySnapshot, currentIndex: number): void {
	const list = byId(root, 'chapterDirectoryList');
	if (!list) return;
	list.replaceChildren(...snapshot.rows.map((chapter) => directoryRow(root, snapshot.slug, chapter, currentIndex)));
}

/**
 * The baked SSR rows reflect the identity at document serve time, which can
 * differ from the live session (device squeeze re-login happens inside the SPA
 * without a reload). Re-ask the chapter API and correct lock states in place;
 * rows only — the hero keeps its painted identity and the SSR document (SEO
 * carrier) is never touched. Failures keep the current display; playback and
 * full-text requests remain gated by the server.
 */
async function revalidateDirectoryLocks(root: Root, request: number, slug: string, currentIndex: number): Promise<void> {
	const runtime = getRuntime(root);
	if (!runtime?.api.listTopicChapters) return;
	try {
		const response = await runtime.api.listTopicChapters(slug);
		if (!isCurrentDirectoryRequest(root, request, slug)) return;
		const directory = directoryFromResponse(response, slug);
		if (!directory) return;
		const list = byId(root, 'chapterDirectoryList');
		if (!list || lockSignature(list) === lockSignatureOf(directory.rows)) return;
		paintDirectoryRows(root, directory, currentIndex);
	} catch {
		// Stale lock display is cosmetic; the server still denies playback.
	}
}

function lockSignatureOf(rows: DirectoryRow[]): string {
	return rows.map((row) => (row.locked ? '1' : '0')).join('');
}

function lockSignature(list: HTMLElement): string {
	return Array.from(list.querySelectorAll('.chapter-directory-row'))
		.map((row) => (row.classList.contains('is-locked') ? '1' : '0'))
		.join('');
}

function paintDirectoryLoading(root: Root): void {
	paintDirectoryStatus(root, 'Loading chapters…');
}

function paintDirectoryError(root: Root): void {
	const list = byId(root, 'chapterDirectoryList');
	if (!list) return;
	const doc = list.ownerDocument;
	const state = doc.createElement('div');
	state.className = 'empty-state-inline';
	state.setAttribute('role', 'status');
	const message = doc.createElement('p');
	message.textContent = 'We could not load chapters.';
	const retry = doc.createElement('button');
	retry.type = 'button';
	retry.className = 'btn btn-primary btn-sm';
	retry.dataset.protoClick = 'openChapterDirectory()';
	retry.textContent = 'Retry';
	state.appendChild(message);
	state.appendChild(retry);
	list.replaceChildren(state);
}

function paintDirectoryStatus(root: Root, message: string): void {
	const list = byId(root, 'chapterDirectoryList');
	if (!list) return;
	const state = list.ownerDocument.createElement('div');
	state.className = 'empty-state-inline';
	state.setAttribute('role', 'status');
	state.setAttribute('aria-live', 'polite');
	state.textContent = message;
	list.replaceChildren(state);
}

function isCurrentDirectoryRequest(root: Root, request: number, slug: string): boolean {
	return holder(root).__chapterDirectoryRequest === request
		&& getRuntime(root)?.activeCourseId === slug
		&& byId(root, 'chapterDirectoryModal')?.classList.contains('active') === true;
}

function directoryRow(root: Root, slug: string, chapter: DirectoryRow, currentIndex: number): HTMLAnchorElement {
	const doc = (root instanceof Document ? root : root.ownerDocument ?? document);
	const template = doc.createElement('template');
	template.innerHTML = chapterDirectoryRowMarkup(slug, chapter, currentIndex);
	return template.content.firstElementChild as HTMLAnchorElement;
}

export function topicDirectoryRows(topic: TopicRecord): DirectoryRow[] {
	const lockedIndexes = new Set(topic.lockedChapters.map((chapter) => chapter.index));
	return [...topic.previewChapters, ...topic.lockedChapters]
		.filter((chapter) => Number.isInteger(chapter.index) && chapter.index > 0 && Boolean(chapter.title))
		.sort((a, b) => a.index - b.index)
		.map(({ index, title, subtitle, summary, duration, hasAudio, hasVideo }) => ({ index, title, subtitle, summary, duration, locked: lockedIndexes.has(index), hasAudio, hasVideo }));
}

function directoryFromTopic(slug: string, topic: TopicRecord | undefined): DirectorySnapshot | null {
	if (!topic || (topic.slug !== slug && topic.id !== slug)) return null;
	const rows = topicDirectoryRows(topic);
	return rows.length ? { slug: topic.slug, title: topic.title, subtitle: topic.subtitle, category: topic.category, classification: topic.classification, source: topic.source, coverBadge: topic.coverBadge, audioMinutes: topic.audioMinutes, coverImageUrl: topic.coverImageUrl, rows } : null;
}

/**
 * Keep directory rows from the current cache but fill poster-only fields from
 * the exact Home card that initiated this navigation. This is display data
 * only; chapter access and playback continue to use the real API response.
 */
function mergeTopicPresentation(topic: TopicRecord | undefined, preview: TopicRecord | undefined): TopicRecord | undefined {
	if (!topic || !preview || topic.slug !== preview.slug) return topic ?? preview;
	return {
		...topic,
		title: preview.title || topic.title,
		subtitle: preview.subtitle || topic.subtitle,
		category: preview.category || topic.category,
		classification: preview.classification ?? topic.classification,
		source: preview.source ?? topic.source,
		coverBadge: preview.coverBadge ?? topic.coverBadge,
		coverImageUrl: preview.coverImageUrl ?? topic.coverImageUrl,
		coverGradient: preview.coverGradient || topic.coverGradient,
		audioMinutes: preview.audioMinutes || topic.audioMinutes,
	};
}

function readPageModelDirectory(root: Root, expectedSlug: string): DirectorySnapshot | null {
	const doc = root instanceof Document ? root : root.ownerDocument ?? document;
	const serialized = doc.querySelector('#page-model')?.textContent;
	if (!serialized) return null;
	try {
		const model = JSON.parse(serialized) as { topic?: unknown; directory?: unknown };
		const topic = record(model.topic);
		const slug = text(topic?.slug);
		const id = text(topic?.id);
		if (!topic || (!slug || (slug !== expectedSlug && id !== expectedSlug)) || !Array.isArray(model.directory)) return null;
		const rows = model.directory.flatMap((candidate) => {
			const chapter = record(candidate);
			const index = typeof chapter?.index === 'number' && Number.isInteger(chapter.index) ? chapter.index : 0;
			const title = text(chapter?.title);
			if (index <= 0 || !title) return [];
			return [{ index, title, subtitle: text(chapter?.subtitle), summary: text(chapter?.summary), duration: text(chapter?.duration), locked: chapter?.accessLabel === 'subscriber', hasAudio: chapter?.hasAudio === true, hasVideo: chapter?.hasVideo === true }];
		});
		return rows.length ? { slug, title: text(topic.title) ?? 'Chapters', subtitle: text(topic.subtitle), category: text(topic.category), classification: record(topic.classification) as TopicRecord['classification'], source: text(topic.source) as TopicRecord['source'], coverBadge: badge(topic.coverBadge), audioMinutes: number(topic.audioMinutes), coverImageUrl: text(topic.coverImageUrl), rows } : null;
	} catch {
		return null;
	}
}

function directoryFromResponse(response: { slug?: unknown; chapters?: unknown }, expectedSlug: string): DirectorySnapshot | null {
	const slug = text(response.slug) ?? expectedSlug;
	if (!Array.isArray(response.chapters)) return null;
	const rows = response.chapters.flatMap((candidate) => {
		const chapter = record(candidate);
		const index = typeof chapter?.index === 'number' && Number.isInteger(chapter.index) ? chapter.index : 0;
		const title = text(chapter?.title);
		return index > 0 && title ? [{ index, title, subtitle: text(chapter?.subtitle), summary: text(chapter?.summary), duration: text(chapter?.duration), locked: record(chapter?.access)?.allowed === false, hasAudio: chapter?.hasAudio === true, hasVideo: chapter?.hasVideo === true }] : [];
	});
	return rows.length ? { slug, title: 'Chapters', rows } : null;
}


function mergeDirectory(pageModel: DirectorySnapshot | null, cached: DirectorySnapshot | null): DirectorySnapshot | null {
	if (!pageModel) return cached;
	if (!cached || cached.slug !== pageModel.slug) return pageModel;
	return {
		...pageModel,
		coverBadge: pageModel.coverBadge ?? cached.coverBadge,
		classification: pageModel.classification ?? cached.classification,
		source: pageModel.source ?? cached.source,
		audioMinutes: pageModel.audioMinutes ?? cached.audioMinutes,
		coverImageUrl: pageModel.coverImageUrl ?? cached.coverImageUrl,
	};
}

function loadDirectoryReactions(root: Root, topicId: string, copy: HTMLElement | null): void {
	const runtime = getRuntime(root);
	if (!copy || !runtime?.api.listFavorites || !runtime.api.listTopicVotes) return;
	const sequence = (holder(root).__chapterDirectoryReactionRequest ?? 0) + 1;
	holder(root).__chapterDirectoryReactionRequest = sequence;
	const favoriteVersion = topicFavoriteVersion(root, topicId);
	const voteVersion = topicVoteVersion(root, topicId);
	const buttons = Array.from(copy.querySelectorAll<HTMLButtonElement>('.topic-actions .action-btn'));
	buttons.forEach((button) => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
	void Promise.all([favoriteStateForTopic(runtime.api, topicId), runtime.api.listTopicVotes()]).then(([favorite, votes]) => {
		if (holder(root).__chapterDirectoryReactionRequest !== sequence || getRuntime(root)?.activeCourseId !== topicId || topicFavoriteVersion(root, topicId) !== favoriteVersion || topicVoteVersion(root, topicId) !== voteVersion) return;
		const vote = ((votes as { votes?: Array<{ topicId?: string; voteValue?: number }> }).votes ?? []).find((item) => item.topicId === topicId)?.voteValue ?? 0;
		for (const button of buttons) {
			if (button.classList.contains('fav-btn')) button.classList.toggle('active', favorite);
			if (button.classList.contains('like-btn')) button.classList.toggle('active', vote === 1);
			if (button.classList.contains('dislike-btn')) button.classList.toggle('active', vote === -1);
			button.setAttribute('aria-pressed', String(button.classList.contains('active')));
		}
	}).catch(() => undefined).finally(() => {
		if (holder(root).__chapterDirectoryReactionRequest !== sequence) return;
		buttons.forEach((button) => { button.disabled = false; button.removeAttribute('aria-busy'); });
	});
}

async function favoriteStateForTopic(api: NonNullable<ReturnType<typeof getRuntime>>['api'], topicId: string): Promise<boolean> {
	let cursor: string | null = null;
	const seenCursors = new Set<string>();
	for (;;) {
		const response = await api.listFavorites({ limit: 20, cursor }) as {
			favorites?: Array<{ topicId?: string; topicSlug?: string }>;
			hasMore?: boolean;
			nextCursor?: string | null;
		};
		if ((response.favorites ?? []).some((item) => item.topicId === topicId || item.topicSlug === topicId)) return true;
		const nextCursor = response.nextCursor;
		if (!response.hasMore || !nextCursor || seenCursors.has(nextCursor)) return false;
		seenCursors.add(nextCursor);
		cursor = nextCursor;
	}
}

function record(value: unknown): Record<string, unknown> | null { return value && typeof value === 'object' ? value as Record<string, unknown> : null; }
function text(value: unknown): string | undefined { return typeof value === 'string' && value.trim() ? value.trim() : undefined; }
function number(value: unknown): number | undefined { return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined; }
function badge(value: unknown): DirectorySnapshot['coverBadge'] { return value === 'github' || value === 'new' || value === 'trending' ? value : undefined; }
